import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';
import { formatViews, getSectionName, shortDate } from '@/utils/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'list' | 'compact';
}

export default function ArticleCard({ article, variant = 'grid' }: ArticleCardProps) {
  const sectionName = getSectionName(article.section);

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-xs font-bold text-primary-700">
          <i className="ri-fire-line" />
        </span>
        <span className="min-w-0">
          <span className="line-clamp-2 block text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 block text-xs text-foreground-500">
            {sectionName} · {formatViews(article.views)} 阅读
          </span>
        </span>
      </Link>
    );
  }

  if (variant === 'list') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid cursor-pointer grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:grid-cols-12 md:p-5"
      >
        <div className="relative h-40 w-full overflow-hidden rounded-md md:col-span-4 md:h-32">
          <img
            src={article.cover}
            alt={article.coverAlt}
            className="h-full w-full object-top"
          />
        </div>
        <div className="md:col-span-8">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">{sectionName}</span>
            <span>{shortDate(article.date)}</span>
            <span>· {article.readTime} 分钟</span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-48 w-full overflow-hidden">
        <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-top" />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-secondary-900 backdrop-blur">
          {sectionName}
        </span>
        {article.hot && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line" /> 热
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span>{shortDate(article.date)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" /> {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}